import React, { useState, useEffect, useRef } from 'react';
import { getCurrentWeather } from '../services/weatherApiReact';
import './WeatherHeader.css';

// Get cached data from window
const cachedData = window.ohtWeatherHeaderCachedData || {};
console.log('WeatherHeader: Initial cachedData from window:', cachedData);

// Extract preloaded data details at the top level of the module for clarity
const initialTempFromPreload = cachedData.temperature !== null && cachedData.temperature !== undefined ? Math.round(cachedData.temperature) : null;
const initialConditionFromPreload = cachedData.condition || '';
const preloadedDataZipcode = cachedData.default_zipcode_for_preload || null;

// Determine if the preloaded data is fundamentally invalid (e.g., no temp or error condition)
const isPreloadedDataInvalid = 
    initialTempFromPreload === null || 
    initialConditionFromPreload === '' || 
    initialConditionFromPreload.toLowerCase().includes('unavailable') || 
    initialConditionFromPreload.toLowerCase().includes('n/a');

const WeatherHeader = () => {
  // Initialize state with preloaded data. These will be used if no fetch is needed on mount.
  const [temperature, setTemperature] = useState(initialTempFromPreload);
  const [condition, setCondition] = useState(initialConditionFromPreload);
  const [loading, setLoading] = useState(isPreloadedDataInvalid); 
  const [error, setError] = useState(null); // No error initially from preload unless we explicitly set one
  
  const [zipcode, setZipcode] = useState(''); // This will be the instance's specific zipcode
  const [linkUrl, setLinkUrl] = useState('');
  const [fetchAttempts, setFetchAttempts] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current ? containerRef.current.parentElement : null;
    let currentInstanceZipcode = '59911'; // Default fallback
    let currentInstanceUrl = '';
        
    if (container) {
      currentInstanceZipcode = container.dataset.zipcode || '59911';
      currentInstanceUrl = container.dataset.linkUrl || '';
      // console.log('WeatherHeader (useEffect): Instance data from container', { currentInstanceZipcode, currentInstanceUrl });
    } else {
      // console.warn('WeatherHeader (useEffect): Container not found, using defaults for instance.');
    }
    
    setZipcode(currentInstanceZipcode); 
    setLinkUrl(currentInstanceUrl);    
    
    const isForCorrectZipcode = currentInstanceZipcode === preloadedDataZipcode;

    if (isPreloadedDataInvalid || !isForCorrectZipcode) {
      // Fetch if: 
      // 1. Preloaded data itself was invalid (e.g. no temp, or error message as condition)
      // 2. Or, preloaded data was for a DIFFERENT zipcode than this instance requires.
      console.log(`WeatherHeader (useEffect): Triggering loadWeatherData. Reason: isPreloadedDataInvalid=${isPreloadedDataInvalid}, isForCorrectZipcode=${isForCorrectZipcode} (Instance: ${currentInstanceZipcode}, Preloaded for: ${preloadedDataZipcode})`);
      if (!loading) setLoading(true); // Ensure loading is true if we are about to fetch
      loadWeatherData(currentInstanceZipcode);
    } else {
      // Preloaded data is valid AND is for this instance's zipcode.
      // No fetch needed on mount. State is already set from preloaded values.
      console.log(`WeatherHeader (useEffect): Using preloaded data for ${currentInstanceZipcode}. No fetch needed on mount.`);
      // Ensure error state is clear if preloaded data was good
      if (!isPreloadedDataInvalid) setError(null);
      // setLoading(false); // Already set to false via useState if isPreloadedDataInvalid was false
    }

  }, []); // Empty dependency array: runs once on mount.

  // Load weather data with minimal API calls and retry logic
  const loadWeatherData = async (zipcode) => {
    // setLoading(true); // Moved to useEffect conditional logic or remains true from initial state
    setError(null); 
    const currentAttempt = fetchAttempts + 1; 
    setFetchAttempts(currentAttempt); 

    console.log(`WeatherHeader: loadWeatherData attempt #${currentAttempt} for zipcode ${zipcode}`);

    try {
      const data = await getCurrentWeather(zipcode);
      
      if (data && (data.current || data.observation)) {
        const obs = data.current || data.observation;
        console.log('WeatherHeader: Data received from API:', obs); 

        if (obs.temperature !== undefined) {
          setTemperature(Math.round(obs.temperature));
          setCondition(obs.wxPhraseLong || obs.wxPhraseShort || 'Condition unavailable');
          setError(null); // Clear error on successful data load
        } else {
          console.warn(`WeatherHeader: Temperature data missing for zipcode ${zipcode}. API Response:`, obs);
          setError('Temperature data unavailable.');
          setTemperature(null); 
          setCondition('Weather data incomplete'); 
        }
      } else {
        console.warn(`WeatherHeader: Invalid weather data format from API for zipcode ${zipcode}. API Response:`, data);
        setError('Invalid weather data format.');
        setTemperature(null);
        setCondition('Weather data unavailable');
      }
    } catch (error) { 
      console.error(`WeatherHeader: Error loading weather data for zipcode ${zipcode}:`, error);
      setError('Failed to load weather data.');
      setTemperature(null); 
      setCondition('Weather data unavailable'); 
      if (currentAttempt < 2) {
        console.log(`WeatherHeader: Attempt ${currentAttempt} failed, scheduling retry for ${zipcode}...`);
        setTimeout(() => loadWeatherData(zipcode), 3000); 
      }
    } finally {
      setLoading(false); // Always set loading to false after attempt (success, error, or retry scheduled)
    }
  };

  // Handle click on the weather header
  const handleClick = () => {
    if (linkUrl) {
      window.location.href = linkUrl;
    }
  };

  // Show loading state or error
  if (loading && fetchAttempts < 2) { // Show loading indicator if loading and not yet on final attempt (if retrying)
    // Or, if not retrying, just `if (loading)` might be fine depending on desired UX during retry delay
    return <div className="weather-header-loading" ref={containerRef}></div>;
  }

  if (error) {
    return <div className="weather-header-error" ref={containerRef}>{error}</div>;
  }

  // Determine weather icon based on condition text
  const getWeatherIcon = () => {
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('sun') || conditionLower.includes('clear')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" fill="none" />
          <line x1="12" y1="3" x2="12" y2="5" stroke="currentColor" strokeWidth="2" />
          <line x1="12" y1="19" x2="12" y2="21" stroke="currentColor" strokeWidth="2" />
          <line x1="5" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" />
          <line x1="21" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" />
          <line x1="7.05" y1="7.05" x2="5.63" y2="5.63" stroke="currentColor" strokeWidth="2" />
          <line x1="18.37" y1="18.37" x2="16.95" y2="16.95" stroke="currentColor" strokeWidth="2" />
          <line x1="16.95" y1="7.05" x2="18.37" y2="5.63" stroke="currentColor" strokeWidth="2" />
          <line x1="5.63" y1="18.37" x2="7.05" y2="16.95" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    } else if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" stroke="currentColor" strokeWidth="2" fill="none" />
        </svg>
      );
    } else if (conditionLower.includes('rain') || conditionLower.includes('shower')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" stroke="currentColor" strokeWidth="2" fill="none" />
          <line x1="8" y1="16" x2="8" y2="20" stroke="currentColor" strokeWidth="2" />
          <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" strokeWidth="2" />
          <line x1="16" y1="16" x2="16" y2="20" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    } else if (conditionLower.includes('snow') || conditionLower.includes('flurr')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path d="M20 17.58A5 5 0 0 0 18 8h-1.26A8 8 0 1 0 4 16.25" stroke="currentColor" strokeWidth="2" fill="none" />
          <line x1="8" y1="16" x2="8.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="18" x2="12.01" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="16" x2="16.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    } else {
      // Default weather icon
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" fill="none" />
          <line x1="12" y1="3" x2="12" y2="5" stroke="currentColor" strokeWidth="2" />
          <line x1="12" y1="19" x2="12" y2="21" stroke="currentColor" strokeWidth="2" />
          <line x1="5" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" />
          <line x1="21" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
  };

  // Render the weather header
  return (
    <div 
      className={`weather-header-container ${linkUrl ? 'clickable' : ''}`}
      onClick={handleClick}
      role={linkUrl ? 'button' : undefined}
      tabIndex={linkUrl ? 0 : undefined}
      aria-label={linkUrl ? `Weather: ${temperature !== null ? Math.round(temperature) : '--'}° ${condition} - Click for more details` : undefined}
      ref={containerRef}
    >
      <div className="weather-header-icon">
        {getWeatherIcon()}
      </div>
      <div className="weather-header-temp">
        {temperature !== null ? `${Math.round(temperature)}°` : '--°'}
      </div>
      <div className="weather-header-condition">
        {condition}
      </div>
    </div>
  );
};

export default WeatherHeader; 